import type { LinkState } from '@hgc/link';
import type { StatusLevel } from '@hgc/ui';

/**
 * 界面上显示的链路阶段。
 *
 * 与 `LinkState`（会话层的六种状态）刻意**不是**同一个枚举：会话关心的是协议事实
 * （有没有帧、心跳超没超时），界面关心的是"用户现在能做什么"。
 * 把六个状态直接铺到标题栏，用户要自己把它们归成"连上了 / 没连上 / 出错了"三类；
 * 而那正是这一层该替他做完的事。
 */
export type LinkPhase = 'disconnected' | 'connecting' | 'connected' | 'error';

export const LINK_PHASE_OF_STATE: Readonly<Record<LinkState, LinkPhase>> = {
  idle: 'disconnected',
  connecting: 'connecting',
  // 链路已建立。"还没收到帧"与"机体失联"仍然是**已连接**：传输本身是通的，
  // 把它显示成"未连接"会让用户去拔线重插，而真正的问题是飞控没通电
  silent: 'connected',
  online: 'connected',
  offline: 'connected',
  error: 'error',
};

export interface LinkStatusText {
  readonly title: string;
  readonly level: StatusLevel;
  /**
   * "为什么现在不是正常连接"。**连上了就是 null** ——
   * 一个永远有内容的说明栏，用户三天后就会当它不存在。
   */
  readonly reason: string | null;
}

const REASON_SILENT =
  '链路已经打开，但还没有收到任何帧：对端可能没在发送、波特率/地址不对，或飞控还没上电';
const REASON_OFFLINE = '超过心跳判定阈值没有收到任何帧';
const REASON_ERROR = '链路报告了错误，但底层没有给出原因';

/**
 * 阶段 → 标题栏文字。纯函数：它决定了"用户看到的唯一一句链路状态"，
 * 因此它的输入只有 (阶段, 会话状态, 底层原因)，不读时钟、不读 DOM。
 */
export function describeLink(
  phase: LinkPhase,
  state: LinkState,
  detail: string | undefined,
): LinkStatusText {
  if (phase === 'error') {
    return {
      title: '链路失败',
      level: 'critical',
      reason: nonEmpty(detail) ?? REASON_ERROR,
    };
  }
  if (phase === 'connecting') {
    return { title: '连接中', level: 'info', reason: '正在打开链路…' };
  }
  if (phase === 'connected') {
    if (state === 'online') return { title: '已连接', level: 'ok', reason: null };
    if (state === 'offline') {
      return { title: '已连接 · 机体失联', level: 'warning', reason: nonEmpty(detail) ?? REASON_OFFLINE };
    }
    return { title: '已连接 · 等待心跳', level: 'caution', reason: nonEmpty(detail) ?? REASON_SILENT };
  }
  return { title: '未连接', level: 'neutral', reason: nonEmpty(detail) ?? null };
}

function nonEmpty(value: string | undefined): string | null {
  return value === undefined || value.trim() === '' ? null : value;
}
